import {
  useCallback, useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import type { ModalProps } from '~/modal/modalType.enum';
import { DataLayerEditorModalContainer } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/modals/dataLayerEditorModal.container';
import { useDataLayersTemplates } from '~/store/frontendState/mapTools/dataLayers/useDataLayersTemplates';
import { mapSettingsDataLayersUpdateDataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import {
  useMapSettingsBoundaryGroupsPerDataLayersSelector, useMapSettingsDataLayersInstancesSelector,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import type {
  DataLayerData, DataLayerId,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';

export type DataLayerEditModalProps = ModalProps<{
  dataLayerId: DataLayerId;
}>;

export const DataLayerEditModalContainer = ({
  dataLayerId,
  ...modalProps
}: DataLayerEditModalProps) => {
  const dispatch = useDispatch();
  const allDataLayers = useMapSettingsDataLayersInstancesSelector();
  const boundaryGroupPerLayer = useMapSettingsBoundaryGroupsPerDataLayersSelector();
  const { createNewTemplate, updateDataLayerTemplate } = useDataLayersTemplates();

  const dataLayer = allDataLayers.find((layer) => layer.id === dataLayerId);
  const boundaryGroupId = boundaryGroupPerLayer[dataLayerId];

  const handleSubmit = useCallback((dataLayerData: DataLayerData, alsoSaveTemplate?: boolean) => {
    dispatch(mapSettingsDataLayersUpdateDataLayer({ id: dataLayerId, data: dataLayerData }));
    if (alsoSaveTemplate && dataLayer) {
      const dataLayerDataForTemplate = {
        ...dataLayer,
        data: dataLayerData,
      };
      if (dataLayer?.fromTemplate) {
        updateDataLayerTemplate(dataLayer.fromTemplate, dataLayerDataForTemplate);
      }
      else {
        createNewTemplate(dataLayerDataForTemplate);
      }
    }
    modalProps.onClose();
  }, [createNewTemplate, dataLayer, dataLayerId, dispatch, modalProps, updateDataLayerTemplate]);

  useEffect(() => {
    if (!dataLayer || !boundaryGroupId) {
      modalProps.onClose();
    }
  }, [dataLayer, boundaryGroupId, modalProps]);

  if (!dataLayer || !boundaryGroupId) {
    return null;
  }

  return (
    <DataLayerEditorModalContainer
      initialData={dataLayer.data}
      boundaryGroupId={boundaryGroupId}
      onSubmit={handleSubmit}
      {...modalProps}
    />
  );
};
